import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABEL, imgUrl, PLACEHOLDER_IMGS } from "../lib/format";
import ProyectoModal from "../components/ProyectoModal";
import EmpresaConfigModal from "../components/EmpresaConfigModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ArrowRight, Bell, Building2, Copy, FileStack, LayoutGrid, LogOut, MapPin, Pencil, Plus, Settings, Trash2, Gauge } from "lucide-react";

const MARQUEE = "GESTIÓN INMOBILIARIA · TRAMITOLOGÍA · PERMISOLOGÍA · DESARROLLO DE PRODUCTO · FINANCIAMIENTO · TAREAS Y ACUERDOS · MASTER PLAN · LICENCIAS · AUDITORÍA · ALERTAS · ";

function ProjectCard({ p, index, onEnter, isAdmin, onEdit, onDuplicate, onDelete }) {
  const img = p.imagen ? imgUrl(p.imagen) : PLACEHOLDER_IMGS[index % PLACEHOLDER_IMGS.length];
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.08 + index * 0.07, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`project-card-${p.id}`}
      onClick={() => onEnter(p.id)}
      className="relative rounded-xl overflow-hidden group cursor-pointer bg-[#002B49] border border-white/10 hover:border-[#00A3B5]/60 transition-colors shadow-lg">
      <div className="relative h-52 overflow-hidden">
        <img src={img} alt={p.nombre} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = PLACEHOLDER_IMGS[index % PLACEHOLDER_IMGS.length]; }} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#002B49] via-[#002B49]/30 to-transparent" />
        {isAdmin && (
          <div className="absolute top-3 right-3 flex gap-1.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
            <button data-testid={`project-edit-${p.id}`} title="Editar proyecto y foto"
              onClick={(e) => { e.stopPropagation(); onEdit(p); }}
              className="w-8 h-8 rounded-md bg-black/50 backdrop-blur text-white hover:bg-[#00A3B5] flex items-center justify-center transition-colors">
              <Pencil size={13} />
            </button>
            <button data-testid={`project-duplicate-${p.id}`} title="Duplicar como plantilla"
              onClick={(e) => { e.stopPropagation(); onDuplicate(p); }}
              className="w-8 h-8 rounded-md bg-black/50 backdrop-blur text-white hover:bg-[#00A3B5] flex items-center justify-center transition-colors">
              <Copy size={13} />
            </button>
            <button data-testid={`project-delete-${p.id}`} title="Eliminar proyecto"
              onClick={(e) => { e.stopPropagation(); onDelete(p); }}
              className="w-8 h-8 rounded-md bg-black/50 backdrop-blur text-white hover:bg-red-600 flex items-center justify-center transition-colors">
              <Trash2 size={13} />
            </button>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-5">
          {p.ubicacion && (
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#EAD8C0] font-bold mb-1 flex items-center gap-1">
              <MapPin size={10} /> {p.ubicacion}
            </p>
          )}
          <h2 className="font-display text-2xl font-bold text-white leading-tight">{p.nombre}</h2>
        </div>
      </div>
      <div className="p-5 pt-4">
        {p.descripcion && <p className="text-white/50 text-xs mb-4 line-clamp-2">{p.descripcion}</p>}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="bg-white/5 border border-white/10 rounded-md px-2 py-2 text-center">
            <p className="font-display text-lg font-bold text-white leading-none" data-testid={`kpi-avance-${p.id}`}>{p.avance_global}%</p>
            <p className="text-[9px] uppercase tracking-wider text-white/50 mt-1">Avance</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-md px-2 py-2 text-center">
            <p className="font-display text-lg font-bold text-white leading-none flex items-center justify-center gap-1"><LayoutGrid size={12} className="text-[#00A3B5]" />{p.num_upes}</p>
            <p className="text-[9px] uppercase tracking-wider text-white/50 mt-1">UPEs</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-md px-2 py-2 text-center">
            <p className="font-display text-lg font-bold text-white leading-none flex items-center justify-center gap-1"><FileStack size={12} className="text-[#00A3B5]" />{p.tramites_activos}</p>
            <p className="text-[9px] uppercase tracking-wider text-white/50 mt-1">Activos</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-md px-2 py-2 text-center">
            <p className="font-display text-lg font-bold text-white leading-none flex items-center justify-center gap-1">
              <Bell size={12} className={p.alertas > 0 ? "text-amber-400" : "text-emerald-400"} />{p.alertas}</p>
            <p className="text-[9px] uppercase tracking-wider text-white/50 mt-1">Alertas</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[#00A3B5] font-bold text-xs uppercase tracking-widest">
          Entrar al proyecto
          <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-2" />
        </div>
      </div>
    </motion.div>
  );
}

export default function Home() {
  const [empresa, setEmpresa] = useState(null);
  const [props, setProps] = useState(null);
  const [projModal, setProjModal] = useState({ open: false, proyecto: null });
  const [cfgOpen, setCfgOpen] = useState(false);
  const [dup, setDup] = useState(null); // proyecto a duplicar
  const [dupNombre, setDupNombre] = useState("");
  const [dupLoading, setDupLoading] = useState(false);
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();

  const load = () => {
    api.get("/empresa").then((r) => setEmpresa(r.data)).catch(() => {});
    api.get("/properties").then((r) => setProps(r.data));
  };
  useEffect(() => { load(); }, []);

  const eliminar = async (p) => {
    if (!window.confirm(`¿Eliminar el proyecto "${p.nombre}"? Se borrarán sus UPEs, trámites y documentos. Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/properties/${p.id}`);
      toast.success("Proyecto eliminado");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const duplicar = async () => {
    setDupLoading(true);
    try {
      await api.post(`/properties/${dup.id}/duplicar`, { nombre: dupNombre });
      toast.success("Proyecto duplicado como plantilla");
      setDup(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    setDupLoading(false);
  };

  const portada = empresa?.portada ? imgUrl(empresa.portada) : null;

  return (
    <div className="min-h-screen flex flex-col bg-[#001D33]">
      {/* Barra superior */}
      <header className="flex items-center justify-between px-4 sm:px-8 py-4 border-b border-white/10 z-20 bg-[#001D33]/90 backdrop-blur sticky top-0">
        <div className="flex items-center gap-3 min-w-0">
          {empresa?.logo ? (
            <img src={imgUrl(empresa.logo)} alt="Logo" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-9 h-9 rounded-md object-contain bg-white/10 p-0.5" data-testid="empresa-logo" />
          ) : (
            <div className="w-9 h-9 rounded-md bg-[#00A3B5] flex items-center justify-center flex-shrink-0">
              <Building2 className="text-white" size={18} />
            </div>
          )}
          <div className="min-w-0">
            <p className="font-display font-bold text-white leading-none truncate" data-testid="empresa-nombre">{empresa?.nombre || "..."}</p>
            <p className="text-white/40 text-[10px] tracking-widest uppercase mt-0.5 truncate">{empresa?.lema || "Selecciona un proyecto"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="text-right hidden sm:block">
            <p className="text-white text-sm font-semibold leading-none">{user?.nombre}</p>
            <p className="text-white/40 text-xs mt-1">{ROLE_LABEL[user?.role]}</p>
          </div>
          {["admin", "financiero"].includes(user?.role) && (
            <button data-testid="ceo-dashboard-button" onClick={() => navigate("/ceo")}
              className="flex items-center gap-2 text-[#EAD8C0] hover:text-white transition-colors px-3 py-1.5 rounded-md border border-[#EAD8C0]/30 hover:border-[#EAD8C0]/70 text-xs font-bold uppercase tracking-wider"
              title="Tablero de control ejecutivo con KPIs de todos los proyectos">
              <Gauge size={14} /> <span className="hidden sm:inline">Resumen Ejecutivo</span>
            </button>
          )}
          {isAdmin && (
            <button data-testid="empresa-config-button" onClick={() => setCfgOpen(true)}
              className="text-white/50 hover:text-white transition-colors p-2" title="Configurar empresa (nombre, logo, portada)">
              <Settings size={18} />
            </button>
          )}
          <button data-testid="logout-button" onClick={async () => { await logout(); navigate("/login"); }}
            className="text-white/50 hover:text-white transition-colors p-2" title="Cerrar sesión">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {/* Portada de la empresa */}
      <div className="relative overflow-hidden" style={{ minHeight: portada ? "240px" : "120px" }}>
        {portada && (
          <>
            <img src={portada} alt="Portada" onError={(e) => { e.currentTarget.style.display = "none"; }} className="absolute inset-0 w-full h-full object-cover" data-testid="empresa-portada" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#001D33] via-[#001D33]/60 to-[#001D33]/20" />
          </>
        )}
        <div className="relative px-4 sm:px-8 py-8 sm:py-12 flex flex-col justify-end h-full">
          <div className="flex items-center gap-4">
            {empresa?.logo && (
              <img src={imgUrl(empresa.logo)} alt="Logo empresa" data-testid="hero-logo"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl object-contain bg-white/90 p-1.5 shadow-lg" />
            )}
            <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              className="font-display text-3xl sm:text-5xl font-bold text-white leading-none">
              {empresa?.nombre || "\u00a0"}
            </motion.h1>
          </div>
          {empresa?.lema && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              className="text-white/60 mt-2 text-sm sm:text-base max-w-xl">{empresa.lema}</motion.p>
          )}
        </div>
      </div>

      {/* Proyectos */}
      <div className="flex-1 px-4 sm:px-8 pb-10">
        <div className="flex items-center justify-between mb-5 mt-2">
          <p className="text-xs tracking-[0.25em] uppercase text-[#00A3B5] font-bold">
            Proyectos {props ? `(${props.length})` : ""}
          </p>
          {isAdmin && (
            <Button data-testid="nuevo-proyecto-button" onClick={() => setProjModal({ open: true, proyecto: null })}
              className="bg-[#00A3B5] hover:bg-[#008C9C] text-white gap-2 h-9 active:scale-[0.98]">
              <Plus size={15} /> Nuevo proyecto
            </Button>
          )}
        </div>
        {props === null ? (
          <div className="py-20 text-center text-white/40 animate-pulse font-display tracking-widest">CARGANDO PROYECTOS...</div>
        ) : props.length === 0 ? (
          <div data-testid="empty-projects" className="py-16 text-center border-2 border-dashed border-white/10 rounded-xl">
            <Building2 size={40} className="mx-auto text-white/20 mb-4" />
            <p className="font-display text-xl font-bold text-white mb-2">Aún no hay proyectos</p>
            <p className="text-white/50 text-sm mb-6 max-w-md mx-auto">
              Crea tu primer proyecto: dale nombre, foto de portada y sube su Master Plan para empezar a gestionar su tramitología.
            </p>
            {isAdmin && (
              <Button data-testid="crear-primer-proyecto-button" onClick={() => setProjModal({ open: true, proyecto: null })}
                className="bg-[#00A3B5] hover:bg-[#008C9C] text-white gap-2">
                <Plus size={15} /> Crear mi primer proyecto
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {props.map((p, i) => (
              <ProjectCard key={p.id} p={p} index={i} isAdmin={isAdmin}
                onEnter={(id) => navigate(`/p/${id}/dashboard`)}
                onEdit={(pr) => setProjModal({ open: true, proyecto: pr })}
                onDuplicate={(pr) => { setDup(pr); setDupNombre(`${pr.nombre} (copia)`); }}
                onDelete={eliminar} />
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-white/10 py-2.5 overflow-hidden whitespace-nowrap">
        <div className="inline-block animate-marquee">
          <span className="text-white/25 text-[11px] tracking-[0.25em] font-semibold">{MARQUEE + MARQUEE}</span>
        </div>
      </div>

      <ProyectoModal open={projModal.open} proyecto={projModal.proyecto}
        onClose={() => setProjModal({ open: false, proyecto: null })} onSaved={load} />
      <EmpresaConfigModal open={cfgOpen} empresa={empresa} onClose={() => setCfgOpen(false)} onSaved={load} />

      <Dialog open={!!dup} onOpenChange={(v) => !v && setDup(null)}>
        <DialogContent className="max-w-sm" data-testid="duplicar-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-[#002B49]">Duplicar como plantilla</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">Se copiarán la estructura, UPEs y trámites de “{dup?.nombre}” con estatus reiniciados.</p>
          <Input data-testid="duplicar-nombre-input" value={dupNombre} onChange={(e) => setDupNombre(e.target.value)} placeholder="Nombre del nuevo proyecto" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDup(null)}>Cancelar</Button>
            <Button data-testid="duplicar-confirmar-button" onClick={duplicar} disabled={dupLoading || !dupNombre.trim()}
              className="bg-[#00A3B5] hover:bg-[#008C9C] text-white">
              {dupLoading ? "Duplicando..." : "Duplicar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
